import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'
import { useCommonStore } from '@/stores/common.ts'
import { useImageSize } from './useImageSize.ts'
import { nextTick } from 'vue'
import { checkBlock, deletePrefix } from '@/views/editor/composable/useNoteUtils'
import { createBlockEvent } from '@/views/editor/composable/useBlockCreate'
import { directSocketSave } from '@/views/editor/composable/useSocketSave'
import { newBlockEvent } from '@/views/editor/composable/useBlockEvent'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { axiosRequest } from '@/lib/axios/axios.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'

export function useImage() {
	async function setImageBlock(base64: string, imageDimension: any) {
		const history = new UseHistory()
		const { selector, newBlockId, noteType } = storeToRefs(useEditorStore())
		const { open } = useCommonStore()

		if (!checkBlock()) {
			await createBlockEvent('next')
		}
		await nextTick()
		const blockCheck = selector.value

		if (!blockCheck) {
			open('error', '이미지를 추가할 블록을 선택해 주세요.', '', null)
			return
		}
		selector.value.dataset.type = 'image'
		selector.value.setAttribute('contenteditable', 'false')
		selector.value.innerHTML = `<div class="image--container" style="width: ${imageDimension.width}px;" data-height="${imageDimension.height}"><img draggable="false" style="width: ${imageDimension.width}px;" data-height="${imageDimension.height}" src="${base64}" alt="" /></div>`

		await nextTick()
		await directSocketSave('image', selector.value)
		await nextTick()

		newBlockEvent()
		// md 규칙: 이미지 추가도 undo 저장
		history.push()
	}

	function init() {
		const { enterSelector } = storeToRefs(useEditorStore())
		if (!enterSelector.value) return
		if (enterSelector.value.dataset.avatarLocked === 'true') return

		const imageContainer: HTMLDivElement = enterSelector.value.querySelector('.image--container')
		if (!imageContainer) return

		const widthElement = imageContainer.querySelector('.image--container__width')
		const heightElement = imageContainer.querySelector('.image--container__height')
		const widthBothElement = imageContainer.querySelector('.image--container__both-width')
		const heightBothElement = imageContainer.querySelector('.image--container__both-height')

		if (!widthBothElement && !heightBothElement) {
			useImageSize().setBoth()
		}

		if (!widthElement) {
			useImageSize().setWidth()
		}

		if (!heightElement) {
			useImageSize().setHeight()
		}
	}

	function reInit() {
		const { enterSelector } = storeToRefs(useEditorStore())
		if (!enterSelector.value) return
		if (enterSelector.value.dataset.avatarLocked === 'true') return

		const imageContainer: HTMLDivElement = enterSelector.value.querySelector('.image--container')
		if (!imageContainer) return

		deleteSizeElement()

		const widthElement = imageContainer.querySelector('.image--container__width')
		const heightElement = imageContainer.querySelector('.image--container__height')
		const widthBothElement = imageContainer.querySelector('.image--container__both-width')
		const heightBothElement = imageContainer.querySelector('.image--container__both-height')

		if (!widthBothElement && !heightBothElement) {
			useImageSize().setBoth()
		}

		if (!widthElement) {
			useImageSize().setWidth()
		}

		if (!heightElement) {
			useImageSize().setHeight()
		}
	}

	function deleteSizeElement() {
		const { isImageSize } = storeToRefs(useEditorStore())

		if (!isImageSize.value) {
			const resizeWidthElement: any = document.querySelector('.image--container__width')
			const resizeHeightElement: any = document.querySelector('.image--container__height')
			const resizeBothWidthElement: any = document.querySelector('.image--container__both-width')
			const resizeBothHeightElement: any = document.querySelector('.image--container__both-height')

			if (resizeWidthElement) resizeWidthElement.remove()
			if (resizeHeightElement) resizeHeightElement.remove()
			if (resizeBothWidthElement) resizeBothWidthElement.remove()
			if (resizeBothHeightElement) resizeBothHeightElement.remove()
		}
	}

	async function setTableCellImage(imageUrl: string, targetCell?: HTMLElement) {
		const history = new UseHistory()
		const { cellTarget, noteType } = storeToRefs(useEditorStore())

		const cell = targetCell || cellTarget.value
		if (!cell) return

		cell.dataset.type = 'image'

		const tdHeight = cell.getBoundingClientRect().height
		cell.style.height = `${tdHeight}px`

		const cellDiv = cell.querySelector('div')
		if (!cellDiv) return

		cellDiv.setAttribute('contenteditable', 'false')
		cellDiv.removeAttribute('style')
		cellDiv.style.width = '100%'
		cellDiv.style.height = '100%'
		cellDiv.style.position = 'relative'
		cellDiv.style.overflow = 'hidden'
		cellDiv.innerHTML = `<img draggable="false" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" src="${imageUrl}" alt="" /><i class="ico ico--table__close" style="position: absolute; top: 2px; right: 2px; z-index: 1;" data-testid="table-close-btn"></i>`

		await nextTick()

		if (noteType.value === 'Project') {
			await directSocketSave('cell', cell)
		}

		await nextTick()

		const table = cell.closest('table') as HTMLTableElement
		if (table) {
			const createTable = useCreateTable()
			createTable.tableShapes(table)
			createTable.selector()
		}

		history.push()
	}

	function openTableCellImageUpload() {
		const { cellTarget, noteType, noteUid } = storeToRefs(useEditorStore())
		const { open, lang } = useCommonStore()

		if (!cellTarget.value) return

		const capturedCell = cellTarget.value
		const capturedBlock = capturedCell.closest('.editor--block')
		if (!capturedBlock) return

		const fileInput = document.createElement('input')
		fileInput.type = 'file'
		fileInput.accept = 'image/*'
		fileInput.style.display = 'none'
		document.body.appendChild(fileInput)

		const removeFileInput = () => {
			if (fileInput.parentNode) fileInput.remove()
		}

		fileInput.addEventListener('change', async (event: Event) => {
			const target = event.target as HTMLInputElement
			const file = target.files?.[0]
			if (!file) {
				removeFileInput()
				return
			}

			try {
				const type = noteType.value === 'Project' ? 'project' : 'template'
				const formData = new FormData()
				formData.append('noteUid', noteUid.value)
				formData.append('blockUid', deletePrefix(capturedBlock.id))
				formData.append('image', file)

				const res: any = await axiosRequest(`/v1/${type}/image/upload`, formData)
				const { noteImages } = res
				const imageUrl = noteImages[0].imageUrl

				await setTableCellImage(imageUrl, capturedCell)
			} catch (error: any) {
				if (error?.response?.statusCode === 413 || error?.status === 413) {
					open('error', lang.modal.error.title, lang.modal.projectList.errFileOverSize, null)
				}
			} finally {
				removeFileInput()
			}
		})

		fileInput.addEventListener('cancel', removeFileInput)

		fileInput.click()
	}

	async function setTableCellPdf(fileUrl: string, fileName: string, targetCell?: HTMLElement) {
		const history = new UseHistory()
		const { cellTarget, noteType } = storeToRefs(useEditorStore())

		const cell = targetCell || cellTarget.value
		if (!cell) return

		cell.dataset.type = 'pdf'
		cell.dataset.pdfUrl = fileUrl
		cell.dataset.pdfName = fileName

		const cellDiv = cell.querySelector('div')
		if (!cellDiv) return

		cellDiv.setAttribute('contenteditable', 'false')
		cellDiv.removeAttribute('style')
		cellDiv.style.width = '100%'
		cellDiv.style.height = '100%'
		cellDiv.style.position = 'relative'
		cellDiv.style.display = 'flex'
		cellDiv.style.alignItems = 'center'
		cellDiv.style.gap = '6px'
		cellDiv.style.padding = '0 8px'
		cellDiv.style.cursor = 'pointer'
		cellDiv.innerHTML = `<i class="ico ico--file__pdf" style="flex-shrink: 0; width: 20px; height: 20px;"></i><span class="table--pdf__name" style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; color: #181e26;">${fileName}</span><i class="ico ico--table__close" style="flex-shrink: 0; z-index: 1;" data-testid="table-close-btn"></i>`

		await nextTick()

		if (noteType.value === 'Project') {
			await directSocketSave('cell', cell)
		}

		await nextTick()

		const table = cell.closest('table') as HTMLTableElement
		if (table) {
			const createTable = useCreateTable()
			createTable.tableShapes(table)
			createTable.selector()
		}

		history.push()
	}

	function openTableCellPdfUpload() {
		const { cellTarget, noteType, noteUid } = storeToRefs(useEditorStore())
		const { open, lang } = useCommonStore()

		if (!cellTarget.value) return

		const capturedCell = cellTarget.value
		const capturedBlock = capturedCell.closest('.editor--block')
		if (!capturedBlock) return

		const fileInput = document.createElement('input')
		fileInput.type = 'file'
		fileInput.accept = '.pdf'
		fileInput.style.display = 'none'
		document.body.appendChild(fileInput)

		const removeFileInput = () => {
			if (fileInput.parentNode) fileInput.remove()
		}

		fileInput.addEventListener('change', async (event: Event) => {
			const target = event.target as HTMLInputElement
			const file = target.files?.[0]
			if (!file) {
				removeFileInput()
				return
			}

			try {
				const type = noteType.value === 'Project' ? 'project' : 'template'
				const formData = new FormData()
				formData.append('noteUid', noteUid.value)
				formData.append('blockUid', deletePrefix(capturedBlock.id))
				formData.append('image', file)

				const res: any = await axiosRequest(`/v1/${type}/image/upload`, formData)
				const { noteImages } = res
				const fileUrl = noteImages[0].imageUrl

				await setTableCellPdf(fileUrl, file.name, capturedCell)
			} catch (error: any) {
				if (error?.response?.statusCode === 413 || error?.status === 413) {
					open('error', lang.modal.error.title, lang.modal.projectList.errFileOverSize, null)
				}
			} finally {
				removeFileInput()
			}
		})

		fileInput.addEventListener('cancel', removeFileInput)

		fileInput.click()
	}

	return {
		init,
		reInit,
		deleteSizeElement,
		setImageBlock,
		setTableCellImage,
		openTableCellImageUpload,
		setTableCellPdf,
		openTableCellPdfUpload,
	}
}
